/**
 * useActiveObjective — Hook centralisé pour l'objectif pédagogique actif
 * ======================================================================
 * 
 * SOURCE UNIQUE pour l'état activeObjective dans toute l'application.
 * 
 * Responsabilités:
 * - Mapping API → état local (structure normalisée)
 * - Capture de la progression initiale (P1.5-C MOA: figée au 1er chargement)
 * - Mise à jour de la progression (endSession)
 * - Callback stable pour ActiveObjectiveBanner
 * 
 * Consommateurs:
 * - CoachVocal.js (session vocale certifiante)
 * - EntrainementEcrit.js (entraînement écrit)
 * 
 * Mars 2026: Centralisation — zéro duplication du mapping API.
 */
import { useState, useRef, useCallback } from 'react';

/**
 * @param {Object} options
 * @param {boolean} options.captureInitialProgress - Si true, capture la progression initiale (P1.5-C)
 * @returns {Object} { activeObjective, initialProgress, isModuleCompleted, handleObjectiveLoaded, updateProgress }
 */
const useActiveObjective = ({ captureInitialProgress = false } = {}) => {
  const [activeObjective, setActiveObjective] = useState(null);
  const [isModuleCompleted, setIsModuleCompleted] = useState(false);
  const initialProgressRef = useRef(null);

  /**
   * Callback stable pour ActiveObjectiveBanner.onObjectiveLoaded
   * Mappe les données API vers la structure activeObjective normalisée.
   */
  const handleObjectiveLoaded = useCallback((data) => {
    if (data.module_completed) {
      setIsModuleCompleted(true);
      setActiveObjective(null);
      return;
    }

    if (data.has_active_objective) {
      setActiveObjective({
        id: data.objective_id,
        title: data.objective_title,
        description: data.objective_description,
        progress: data.progress,
        criteria: data.evaluation_criteria
      });

      // P1.5-C MOA: Capturer la progression initiale UNE SEULE FOIS
      if (captureInitialProgress && initialProgressRef.current === null) {
        initialProgressRef.current = data.progress || 0;
      }
    }
  }, [captureInitialProgress]);

  /**
   * Met à jour la progression de l'objectif actif (utilisé par endSession).
   */
  const updateProgress = useCallback((newProgress) => {
    setActiveObjective(prev => prev ? { ...prev, progress: newProgress } : prev);
  }, []);

  return {
    activeObjective,
    initialProgress: initialProgressRef.current,
    initialProgressRef,
    isModuleCompleted,
    handleObjectiveLoaded,
    updateProgress
  };
};

export default useActiveObjective;
